import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { apSyncApi } from "../services/rag-service";
import { useThemeMode } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { getKeycloakToken } from "../services/keycloak";
import { tokenManager } from "../utils/tokenManager";
import { useStoredTheme } from "../utils/theme-context";
import { apFrontendUrl } from "../utils/ap-frontend-url";

// Handshake protocol shared with packages/web's ExternalEmbedPage
// (src/app/routes/embed/external-embed.tsx). The child announces readiness with a
// secret-free READY ping; the parent replies with the real, already-minted AP token
// scoped to the flow's resolved AP project. The token NEVER travels in the iframe
// src/URL - only inside the AUTH postMessage payload, with an exact targetOrigin.
const EXTERNAL_EMBED_READY = "AP_EXTERNAL_EMBED_READY";
const EXTERNAL_EMBED_AUTH = "AP_EXTERNAL_EMBED_AUTH";
// Live light/dark toggle, sent any time AFTER the handshake above (the initial
// mode already travels once, inside the AUTH message itself, to avoid a flash -
// see packages/web's embedding-theme-listener.tsx for the long-lived receiver).
const EXTERNAL_EMBED_THEME_CHANGE = "AP_EXTERNAL_EMBED_THEME_CHANGE";
// On-demand pair, symmetric to READY/AUTH but scoped to just the connection
// auth prefill: the AUTH message's connectionAuthPrefill is a one-time
// snapshot taken at handshake time, which can go stale (the underlying
// Keycloak token has a short TTL) by the time a connection dialog is actually
// opened later in the session. AP's own connection dialog re-asks for a fresh
// one on mount (see connection-auth-prefill-refresh.ts) rather than trusting
// whatever it was handed at boot.
const CONNECTION_AUTH_PREFILL_REQUEST = "AP_CONNECTION_AUTH_PREFILL_REQUEST";
const CONNECTION_AUTH_PREFILL_RESPONSE = "AP_CONNECTION_AUTH_PREFILL_RESPONSE";
// One-way push (no request half - unlike the pair above, this side always
// knows better than AP when a fresh token is due, so it doesn't wait to be
// asked). Keeps the AUTH message's projectId/flowId/token untouched - only
// the context token is replaced. See CONTEXT_TOKEN_REFRESH_INTERVAL_MS below
// for why this exists at all.
const CONTEXT_TOKEN_REFRESH = "AP_CONTEXT_TOKEN_REFRESH";
// rag-agents' own context token (ap_context_token.py) is minted fresh per
// getApSession call with a hard 5-minute TTL (CONTEXT_TOKEN_TTL_SECONDS) and
// nothing re-mints it after the initial handshake - a builder session left
// open past 5 minutes (completely normal usage) silently loses AP's
// per-project tenant narrowing (falls back to coarser, still-safe org-wide
// scoping - see bunyan-context-token.ts's own fallback). Re-minting well
// inside the TTL keeps a real session from ever reaching that fallback at
// all, mirroring the same just-in-time-refresh philosophy tokenManager.js
// already uses for the Keycloak token itself (refresh before it's needed,
// not an ambient poll after the fact).
const CONTEXT_TOKEN_REFRESH_INTERVAL_MS = 3 * 60 * 1000;

// apFrontendUrl (imported above) is the independently-served AP frontend
// (packages/web) this iframe loads. Same origin as AP's own API, so the
// builder's /api calls are same-origin once it boots - the whole reason this
// path exists (no cross-origin per-request token injection, and the builder
// tracks real upstream AP on every rebase with zero re-vendoring).

// Lets a SECRET_TEXT connection dialog inside the embedded builder (e.g. the
// Bunyan LLM piece) skip a manual token paste by suggesting the caller's own
// already-authenticated identity - mirrors what the legacy <Omniflow> bundle's
// BunyanCustomAuth does via a same-origin cookie read, which isn't possible
// here (the builder runs in a cross-origin iframe, with no access to this
// origin's cookies). Reuses the app's own existing token accessor
// (getKeycloakToken) instead of decoding a cookie directly. Returns undefined
// if the caller isn't authenticated yet - the AUTH message just omits the
// field, and AP's dialog falls back to its normal manual-entry input.
//
// Async because it must call tokenManager.refreshIfExpired() first:
// getKeycloakToken() is a PASSIVE read of whatever's currently cached - this
// app's whole token architecture is just-in-time refresh (see
// tokenManager.js's own comment: "Token refresh is now handled just-in-time
// before each request"), triggered only by the axios request interceptor.
// Nothing refreshes it ambiently, so without this call the token handed to a
// connection dialog can already be expired by the time it's used.
async function getConnectionAuthPrefill(currentUser, currentOrganization) {
  try {
    await tokenManager.refreshIfExpired();
  } catch (error) {
    console.error(
      "ApBuilderIframe: token refresh failed before building auth prefill",
      error,
    );
  }
  const token = getKeycloakToken();
  if (!token || !currentUser?.email) {
    return undefined;
  }
  // currentUser here is the raw Keycloak user info (see keycloak.js's
  // getKeycloakUserInfo) - firstName/lastName/username, no single "name"
  // field. Matches AuthContext.jsx's own displayName composition.
  const fullName = `${currentUser.firstName || ""} ${
    currentUser.lastName || ""
  }`.trim();
  const name = fullName || currentUser.username || currentUser.email;
  return {
    // Matches the legacy BunyanCustomAuth's own connection-name suggestion
    // exactly ("{name}<{email}>").
    connectionDisplayName: `${name}<${currentUser.email}>`,
    name,
    secretValue: currentOrganization?.id
      ? `${token},${currentOrganization.id}`
      : token,
    organizationSlug: currentOrganization?.slug,
  };
}

export function ApBuilderIframe({ projectId, flowId, tenantId, realProjectId, onBack }) {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const storedTheme = useStoredTheme();
  const accentColor = storedTheme?.button?.primary?.backgroundColor?.[mode];
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const handleMessage = async (event) => {
      // Only trust messages from the exact AP frontend origin, from this very
      // iframe's own content window - never a wildcard, never a sibling frame.
      if (
        event.origin !== apFrontendUrl ||
        event.source !== iframeRef.current?.contentWindow
      ) {
        return;
      }
      if (event.data?.type === CONNECTION_AUTH_PREFILL_REQUEST) {
        // Secret-bearing reply: exact targetOrigin, never "*" (matches the
        // AUTH message below, unlike the secret-free READY/REQUEST pings).
        const connectionAuthPrefill = await getConnectionAuthPrefill(
          currentUser,
          currentOrganization,
        );
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: CONNECTION_AUTH_PREFILL_RESPONSE,
            data: { connectionAuthPrefill },
          },
          apFrontendUrl,
        );
        return;
      }
      if (event.data?.type !== EXTERNAL_EMBED_READY) {
        return;
      }
      try {
        const apSession = await apSyncApi.getApSession(realProjectId);
        const connectionAuthPrefill = await getConnectionAuthPrefill(
          currentUser,
          currentOrganization,
        );
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: EXTERNAL_EMBED_AUTH,
            data: {
              token: apSession.token,
              // The flow's resolved AP project home (shared-project aware), NOT the
              // caller's own personal projectId from getApSession - matches what the
              // <Omniflow> backup path pushes via apTokenManager.setProjectId.
              projectId,
              flowId,
              // Real org id (org-scoped project) or user id (personal project) - lets
              // the embedded builder tag resources it creates directly (e.g. app
              // connections) with a real, grantable externalId instead of AP's own
              // random fallback, which the Bunyan authorization hook always rejects.
              tenantId,
              // Signed, short-lived {humanId, projectId: realProjectId, role} token
              // (see ap_context_token.py) - lets AP's authorization hook scope
              // listings to THIS real rag-agents project and gate writes by the
              // caller's actual role on it, replacing the old bunyanSessionTenantId
              // hint. Null when realProjectId wasn't provided or minting failed -
              // the hook must never treat a missing token as "full access".
              contextToken: apSession.contextToken,
              // Host shell's CURRENT light/dark mode, so the builder opens in
              // the right mode from its very first paint instead of always
              // dark-then-flip (see THEME_CHANGE below for later toggles).
              theme: mode,
              // This org's primary button color for the current mode, so the
              // embedded builder matches the host shell instead of AP's own
              // static color. Undefined for an org with no custom theme.
              accentColor,
              // Optional suggested identity for a SECRET_TEXT connection
              // dialog (see getConnectionAuthPrefill above). Sent on every
              // handshake regardless of which piece the user ends up
              // connecting - this component doesn't yet know which piece's
              // dialog (if any) will be opened inside this flow, so there's
              // no earlier point to gate it on. Rides the same
              // already-trusted, exact-origin-checked channel as `token`
              // and `contextToken` above; AP only ever surfaces it for a
              // SECRET_TEXT auth prop, never using it otherwise.
              connectionAuthPrefill,
            },
          },
          apFrontendUrl,
        );
        setReady(true);
      } catch (error) {
        console.error("ApBuilderIframe: failed to mint AP session", error);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [
    projectId,
    flowId,
    tenantId,
    realProjectId,
    mode,
    accentColor,
    currentUser,
    currentOrganization,
  ]);

  // Live theme sync AFTER the handshake: the initial mode already travelled
  // once inside the AUTH message above. Once the iframe is ready, push any
  // LATER mode change (e.g. the user clicking the host shell's own dark-mode
  // toggle) straight into that same iframe's contentWindow - see
  // packages/web's embedding-theme-listener.tsx for the receiving side.
  useEffect(() => {
    if (!ready) {
      return;
    }
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: EXTERNAL_EMBED_THEME_CHANGE,
        data: { theme: mode, accentColor },
      },
      apFrontendUrl,
    );
  }, [mode, accentColor, ready]);

  // Proactive context-token re-mint (see CONTEXT_TOKEN_REFRESH_INTERVAL_MS
  // above for why this exists). Re-runs the SAME call the initial handshake
  // uses (apSyncApi.getApSession) - it re-checks the caller's real grant on
  // realProjectId and re-resolves their current role server-side on every
  // call, so this doubles as a live revocation check: a member removed from
  // the project mid-session simply stops getting a usable context token from
  // this point on, without needing a page reload to notice. A failed refresh
  // (network blip, or a genuine 403 if access was revoked) is logged and
  // skipped for this tick only - the existing token keeps aging toward its
  // own expiry and the next tick tries again, matching this integration's
  // established best-effort-with-backstop posture elsewhere (e.g. the AP->
  // rag-agents webhooks) rather than tearing down a working iframe over one
  // missed refresh.
  useEffect(() => {
    if (!ready) {
      return;
    }
    const intervalId = setInterval(async () => {
      try {
        const apSession = await apSyncApi.getApSession(realProjectId);
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: CONTEXT_TOKEN_REFRESH,
            data: { contextToken: apSession.contextToken },
          },
          apFrontendUrl,
        );
      } catch (error) {
        console.error(
          "ApBuilderIframe: context token refresh failed, will retry next interval",
          error,
        );
      }
    }, CONTEXT_TOKEN_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [ready, realProjectId]);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {!ready && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: theme.palette.background.default,
            zIndex: 1,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading builder...
          </Typography>
        </Box>
      )}
      <iframe
        ref={iframeRef}
        title="Flow Builder"
        src={`${apFrontendUrl}/embed/external`}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </Box>
  );
}
